import { getOrderProcessManifest } from '@b-stock/fe-core/bstock-next/queries'
import type { ItemDto, StoreFrontDto } from '@b-stock/listing-api-client'

import type { CombinedListingSearchResult } from '@/queries/CombinedListingSearchResult'

import { isMobileCategoryStorefront } from '../helpers/isMobileCategoryStorefront'
import { resolveStorefrontsForListings } from './resolveStorefrontsForListings'

// Matches FULLSCREEN_MANIFEST_LIMIT in useManifestDatasource.ts (kept as its
// own constant here rather than imported, so this plain data-loading module
// doesn't depend on an ag-grid UI hook module and everything it pulls in).
const PAGE_SIZE = 100

// No "many lots in one request" manifest-items endpoint exists today —
// getManifestItemsByLotId (wrapped by getOrderProcessManifest) is per-lot
// only. The bulk alternative, getManifestExportAsync, accepts many listingIds
// in one call but its customColumns codes are an undocumented backend map
// ("unknown codes render an empty cell" — a silent failure, not an error)
// with no example of valid codes anywhere in this repo, so it isn't used
// here. This fans out one request per listing instead, throttled to this
// many in flight at once so a several-hundred-lot bid sheet doesn't open
// hundreds of concurrent connections.
const CONCURRENCY = 10

// Caps the worst case request volume for one download: beyond this many
// mobile-category listings, per-lot manifest fetching (already up to
// MAX_PAGES_PER_LOT requests per listing) is skipped entirely and every
// listing in the batch falls back to a flat row, rather than silently
// serving a mix of manifest-level and flat rows depending on how far the
// fetch got. The exact number is a starting guess, not measured against
// real traffic — revisit once this ships.
const MAX_MOBILE_MANIFEST_LISTINGS = 200

const runWithConcurrencyLimit = async <T, R>(
  items: T[],
  limit: number,
  fn: (item: T) => Promise<R>
): Promise<R[]> => {
  const results: R[] = new Array<R>(items.length)
  let nextIndex = 0
  const workers = Array.from(
    { length: Math.min(limit, items.length) },
    async () => {
      while (nextIndex < items.length) {
        const i = nextIndex++
        const item = items[i]
        if (item !== undefined) {
          results[i] = await fn(item)
        }
      }
    }
  )
  await Promise.all(workers)
  return results
}

// Safety cap on pages per lot, so a corrupted/runaway `total` from the
// backend can't loop forever, and so one unusually large lot can't on its own
// balloon a download's total request count. A carrier liquidation lot with
// more manifest lines than this (2,000) is not expected in practice.
const MAX_PAGES_PER_LOT = 20

/**
 * Fetches every manifest item for one lot, paging by `offset`/`total` rather
 * than trusting a single page — `getManifestItemsByLotId` caps each response
 * at `limit` (PAGE_SIZE) regardless of how many items the lot actually has,
 * and carrier liquidation lots routinely exceed that.
 */
const fetchAllManifestItemsForLot = async (
  accessToken: string | null,
  lotId: string
): Promise<ItemDto[]> => {
  const items: ItemDto[] = []
  let total = Infinity
  for (let page = 0; items.length < total && page < MAX_PAGES_PER_LOT; page++) {
    const result = await getOrderProcessManifest(accessToken, {
      lotId,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      exclude: ['metadata'],
    })
    total = result.total
    if (!result.items.length) break // safety net against an infinite loop
    items.push(...result.items)
  }
  return items
}

/**
 * Fetches manifest line items (model, grade, carrier, capacity, quantity) for
 * every listing in `listings` that belongs to a mobile-category storefront
 * (AT&T, other carrier liquidations), keyed by listingId — a non-mobile
 * listing keeps today's listing-level sheet instead. `storefront` is the
 * page's own storefront context, used as a shortcut when every listing
 * already belongs to it (the common single-storefront ListView case); a
 * batch spanning multiple storefronts (e.g. the All Listings Page) instead
 * resolves each listing's own storefront via a bulk by-id lookup, so a mobile
 * listing on a mixed-storefront page still gets its manifest data.
 *
 * Also returns an empty map (skipping manifest fetching entirely, for every
 * listing) once the mobile-category subset exceeds
 * `MAX_MOBILE_MANIFEST_LISTINGS` — each lot can already cost up to
 * `MAX_PAGES_PER_LOT` requests on its own, so an unbounded listing count
 * could otherwise fan out into tens of thousands of requests from one click.
 */
export async function fetchMobileManifestItems(
  accessToken: string | null,
  listings: CombinedListingSearchResult[],
  storefront: StoreFrontDto | undefined
): Promise<Map<string, ItemDto[]>> {
  const listingsWithLots = listings.filter((listing) => !!listing.lotId)
  if (listingsWithLots.length === 0) return new Map()

  const storefrontsById = await resolveStorefrontsForListings(
    accessToken,
    listingsWithLots,
    storefront
  )
  const mobileListings = listingsWithLots.filter((listing) =>
    isMobileCategoryStorefront(storefrontsById[listing.storefrontId])
  )
  if (mobileListings.length === 0) return new Map()
  if (mobileListings.length > MAX_MOBILE_MANIFEST_LISTINGS) return new Map()

  const entries = await runWithConcurrencyLimit(
    mobileListings,
    CONCURRENCY,
    async (listing) => {
      try {
        const items = await fetchAllManifestItemsForLot(
          accessToken,
          listing.lotId as string
        )
        return [listing.listingId, items] as const
      } catch {
        // Fail soft — a batch can run up to MAX_MOBILE_MANIFEST_LISTINGS
        // lots, so one bad lot (network blip, a single malformed record)
        // shouldn't abort the rest of an already-in-flight batch. The
        // listing falls back to a plain listing-level row, the same as any
        // listing with no manifest entry at all.
        return [listing.listingId, [] as ItemDto[]] as const
      }
    }
  )

  return new Map(entries)
}
